import type { FilterOptionGroup } from '../components/filter-pill-primitives.js';

/**
 * Separator used to namespace a `GroupedFilterPill` flat selection key as
 * `${groupId}::${value}`. Exported so every consumer that builds or parses a
 * namespaced key (`GroupedFilterPill`, `bucketGroupedFilterValues`, and any
 * app-level adapter merging real filter values into the flat array) shares
 * one definition instead of hand-reimplementing the separator — they must
 * agree byte-for-byte for selection round-tripping to work.
 */
export const GROUPED_FILTER_KEY_SEPARATOR = '::';

/**
 * Builds a namespaced flat-array key for an option within a group, e.g.
 * `namespacedKey('city', 'Kraków')` -> `'city::Kraków'`.
 */
export function namespacedKey(groupId: string, value: string): string {
  return `${groupId}${GROUPED_FILTER_KEY_SEPARATOR}${value}`;
}

/**
 * Splits a namespaced key back into its group id and raw value. Returns
 * `null` when the key has no separator (not a namespaced key at all).
 */
export function parseNamespacedKey(key: string): { groupId: string; value: string } | null {
  const separatorIndex = key.indexOf(GROUPED_FILTER_KEY_SEPARATOR);
  if (separatorIndex === -1) return null;
  return {
    groupId: key.slice(0, separatorIndex),
    value: key.slice(separatorIndex + GROUPED_FILTER_KEY_SEPARATOR.length),
  };
}

/**
 * Buckets a flat list of NAMESPACED selected values back into per-group arrays,
 * keyed by each group's stable `id` (never the translated `heading`).
 *
 * `GroupedFilterPill` stores ALL selected values as one flat array (matching
 * `FilterPanel`'s `value: Record<string, string[]>` model — one array per field
 * id), but namespaces each entry via `namespacedKey` so identical raw values in
 * two different groups (e.g. county "Kraków" and city "Kraków" — every "powiat
 * grodzki" city has county === city) never collide in the flat selection model.
 * Consumers that need to know which group each value came from (e.g. to forward
 * `region`/`county`/`agglomeration`/`city` as separate API params) use this to
 * reconstruct the per-group buckets, with the group prefix stripped so the RAW
 * value is what reaches the API filters.
 *
 * Entries that aren't namespaced for the given group, or whose de-namespaced
 * value doesn't match any option in that group, are dropped.
 */
export function bucketGroupedFilterValues(
  groups: FilterOptionGroup[],
  selected: string[],
): Record<string, string[]> {
  const buckets: Record<string, string[]> = {};
  for (const group of groups) {
    const optionValues = new Set(group.options.map((o) => o.value));
    buckets[group.id] = selected
      .map(parseNamespacedKey)
      .filter((parsed): parsed is { groupId: string; value: string } => parsed !== null)
      .filter((parsed) => parsed.groupId === group.id && optionValues.has(parsed.value))
      .map((parsed) => parsed.value);
  }
  return buckets;
}
